/**
 * Analytics Service
 * Tracks user events and sends to backend API for dashboard analytics.
 * Fire-and-forget - never blocks UI, never waits for response.
 *
 * Backend API Contract:
 * POST {REACT_APP_ANALYTICS_API_URL}/api/analytics/events
 *
 * Common payload (sent with every event):
 * {
 *   event: string,
 *   sessionId: string,
 *   hotelId: string,
 *   device: "mobile" | "desktop",
 *   browser: string,
 *   language: string,
 *   userAgent: string,
 *   pageUrl: string,
 *   referrerSource: string,
 *   timestamp: ISO-8601 string,
 *   data: { ... } // event-specific
 * }
 */

import { getSessionId } from '../utils/trackingParams';

// ─── Config ───────────────────────────────────────────────
const ANALYTICS_API_URL = process.env.REACT_APP_ANALYTICS_API_URL || '';

// ─── Helpers ──────────────────────────────────────────────

const getDeviceType = () => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return isMobile ? 'mobile' : 'desktop';
};

const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Unknown';
};

const getLanguage = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lang') || navigator.language || 'en';
  } catch {
    return 'en';
  }
};

const getReferrerSource = () => {
  try {
    // Check sessionStorage first (persists across internal navigations)
    const stored = sessionStorage.getItem('analytics_referrer_source');
    if (stored) return stored;

    const urlParams = new URLSearchParams(window.location.search);

    // 1. Check utm_campaign for Hotel Ads
    const utmCampaign = urlParams.get('utm_campaign');
    if (utmCampaign && utmCampaign.toLowerCase() === 'hotelads') {
      sessionStorage.setItem('analytics_referrer_source', 'Google Hotel Ads');
      return 'Google Hotel Ads';
    }

    // 2. Check utm_source
    const utmSource = urlParams.get('utm_source');
    if (utmSource) {
      sessionStorage.setItem('analytics_referrer_source', utmSource);
      return utmSource;
    }

    // 3. Check document.referrer
    const referrer = document.referrer;
    if (!referrer || referrer.includes(window.location.hostname)) {
      sessionStorage.setItem('analytics_referrer_source', 'Direct');
      return 'Direct';
    }

    let source = 'Direct';
    if (referrer.includes('google.')) source = 'Google';
    else if (referrer.includes('bing.')) source = 'Bing';
    else if (referrer.includes('yahoo.')) source = 'Yahoo';
    else if (referrer.includes('duckduckgo.')) source = 'DuckDuckGo';
    else if (referrer.includes('facebook.') || referrer.includes('fb.')) source = 'Facebook';
    else if (referrer.includes('instagram.')) source = 'Instagram';
    else {
      try { source = new URL(referrer).hostname; } catch { source = referrer; }
    }

    sessionStorage.setItem('analytics_referrer_source', source);
    return source;
  } catch {
    return 'Direct';
  }
};

// ─── Core ─────────────────────────────────────────────────

/**
 * Build common payload attached to every event
 */
const buildCommonPayload = (hotelId) => ({
  sessionId: getSessionId(),
  hotelId: hotelId || '',
  device: getDeviceType(),
  browser: getBrowserName(),
  language: getLanguage(),
  userAgent: navigator.userAgent,
  pageUrl: window.location.href,
  referrerSource: getReferrerSource(),
  timestamp: new Date().toISOString(),
});

/**
 * Send event to backend API - fire and forget
 */
const sendEvent = (eventName, hotelId, eventData = {}) => {
  try {
    const payload = {
      event: eventName,
      ...buildCommonPayload(hotelId),
      data: eventData,
    };

    // Always save to localStorage for testing/debugging (grouped by hotelId)
    try {
      const storageKey = `analytics_events_${hotelId || 'unknown'}`;
      const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existing.push(payload);
      localStorage.setItem(storageKey, JSON.stringify(existing));
      console.log(`[Analytics] ${eventName}`, payload);
    } catch { }

    if (!ANALYTICS_API_URL) return;

    const url = `${ANALYTICS_API_URL}/api/analytics/events`;
    const body = JSON.stringify(payload);

    // Use fetch with keepalive (sendBeacon includes credentials by default which breaks CORS wildcard)
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit', // Don't send cookies - allows wildcard CORS
      mode: 'cors',
    })
      .then(res => console.log(`[Analytics] ${eventName} sent → status: ${res.status}`))
      .catch(err => console.warn(`[Analytics] ${eventName} failed:`, err.message));
  } catch {
    // Never throw - analytics should never break the app
  }
};

// ─── Event Functions ──────────────────────────────────────

/**
 * Event 1: Page View (Browsing)
 * Fire when: page loads (availability, checkout, etc.)
 */
export const trackPageView = (hotelId, { page = '', hotelName = '' } = {}) => {
  sendEvent('page_view', hotelId, { page, hotelName });
};

/**
 * Event 2: Search
 * Fire when: user clicks "Check Availability"
 */
export const trackSearch = (hotelId, { checkin, checkout, adults, children, promoCode } = {}) => {
  sendEvent('search', hotelId, { checkin, checkout, adults, children, promoCode });
};

/**
 * Event 3: Availability Results (Had Availability)
 * Fire when: availability API returns results after search
 */
export const trackAvailabilityResults = (hotelId, { checkin, checkout, adults, children, totalRooms, availableRooms, soldOut } = {}) => {
  sendEvent('availability_results', hotelId, { checkin, checkout, adults, children, totalRooms, availableRooms, soldOut });
};

/**
 * Event 4: Room Selected (Carted)
 * Fire when: user selects room and clicks "Book Now"
 */
export const trackRoomSelected = (hotelId, { checkin, checkout, adults, children, rooms, totalAmount, currency } = {}) => {
  sendEvent('room_selected', hotelId, { checkin, checkout, adults, children, rooms, totalAmount, currency });
};

/**
 * Event 5: Checkout Started
 * Fire when: checkout page loads
 */
export const trackCheckoutStarted = (hotelId, { checkin, checkout, adults, children, rooms, totalAmount, currency } = {}) => {
  sendEvent('checkout_started', hotelId, { checkin, checkout, adults, children, rooms, totalAmount, currency });
};

/**
 * Event 6: Checkout Form Filled (guest info entered)
 * Fire when: user fills form and clicks proceed/confirm
 */
export const trackCheckoutFormFilled = (hotelId, { email, firstName, lastName, phone, country } = {}) => {
  sendEvent('checkout_form_filled', hotelId, { email, firstName, lastName, phone, country });
};

/**
 * Event 7: Booking Completed
 * Fire when: user reaches confirmation page
 */
export const trackBookingCompleted = (hotelId, { bookingId, checkin, checkout, rooms, totalAmount, currency, email, paymentMethod } = {}) => {
  sendEvent('booking_completed', hotelId, { bookingId, checkin, checkout, rooms, totalAmount, currency, email, paymentMethod });
};

/**
 * Event 8: Booking Failed
 * Fire when: reservation API returns error
 */
export const trackBookingFailed = (hotelId, { errorMessage, errorCode, checkin, checkout, totalAmount, currency } = {}) => {
  sendEvent('booking_failed', hotelId, { errorMessage, errorCode, checkin, checkout, totalAmount, currency });
};

/**
 * Event 9: Checkout Abandoned (tab/window closed during checkout)
 * Fire when: user closes tab or navigates away without completing booking
 */
export const trackCheckoutAbandoned = (hotelId, { checkin, checkout, rooms, totalAmount, currency, email, timeOnPage } = {}) => {
  sendEvent('checkout_abandoned', hotelId, { checkin, checkout, rooms, totalAmount, currency, email, timeOnPage });
};

/**
 * Event 10: Member Unlock (Unlock Member Rate clicked)
 * Fire when: user clicks "Unlock Member Rate" button on secret deal
 */
export const trackMemberUnlock = (hotelId, { ratePlanName, roomName } = {}) => {
  sendEvent('member_unlock', hotelId, { ratePlanName, roomName });
};
